<!-- src/views/teacher/TeacherDashboard/DivWrapperScreen/sections/DivSubsection.vue -->
<template>
	<div class="conferences-section">
		<!--  Центрирующий контейнер -->
		<div class="section-container">
			<!-- секцию header на: -->
			<header class="section-header">
				<h2 class="section-title">Ваши конференции</h2>

				<!-- Фильтр по категории -->
				<div class="category-filter" v-click-outside="closeCategoryDropdown">
					<button
						class="filter-button"
						@click="toggleCategoryDropdown"
						:class="{ active: showCategoryDropdown }"
					>
						<!--  Используем переведённый label -->
						<span>{{ selectedCategoryLabel }}</span>
						<span
							class="dropdown-arrow"
							:class="{ rotated: showCategoryDropdown }"
							>▼</span
						>
					</button>

					<transition name="dropdown">
						<div v-if="showCategoryDropdown" class="dropdown-menu">
							<!-- Пункт "Все категории" -->
							<button
								@click="selectCategory(null)"
								class="dropdown-item"
								:class="{ selected: selectedCategory === null }"
							>
								Все категории
							</button>

							<!-- 🔥 Используем category.value и category.label -->
							<button
								v-for="category in availableCategories"
								:key="category.value"
								@click="selectCategory(category.value)"
								class="dropdown-item"
								:class="{ selected: category.value === selectedCategory }"
							>
								{{ category.label }}
							</button>
						</div>
					</transition>
				</div>

				<!-- 🔥 НОВЫЙ: Переключатель "В эфире" -->
				<label class="toggle-checkbox">
					<input type="checkbox" v-model="showLiveOnly" @change="onToggleLive" />
					<span class="toggle-label live">
						<span class="live-dot"></span>
						В эфире
					</span>
				</label>

				<!-- Переключатель прошедших конференций -->
				<label class="toggle-checkbox">
					<input
						type="checkbox"
						v-model="includePast"
						@change="onTogglePast"
					/>
					<span class="toggle-label">Показать прошедшие</span>
				</label>

				<!-- Навигация слайдера -->
				<div class="slider-nav" v-if="showSliderNav">
					<button
						class="nav-btn"
						@click="slidePrev"
						:disabled="currentSlide === 0"
					>
						‹
					</button>
					<span class="slide-counter"
						>{{ currentSlide + 1 }} / {{ maxSlide + 1 }}</span
					>
					<button
						class="nav-btn"
						@click="slideNext"
						:disabled="currentSlide >= maxSlide"
					>
						›
					</button>
				</div>

				<!-- Кнопка добавления конференции -->
				<button
					class="add-conference-btn"
					title="Создать новую конференцию"
					@click="showCourseSelector = true"
				>
					<span>+</span>
				</button>
			</header>

			<!-- Состояние загрузки -->
			<div v-if="loading" class="loading-state">
				<div class="spinner"></div>
				<p>Загрузка конференций...</p>
			</div>

			<!-- Ошибка -->
			<div v-else-if="error" class="error-state">
				<p>{{ error }}</p>
				<button @click="loadConferences" class="retry-btn">Повторить</button>
			</div>

			<!-- Пустое состояние -->
			<div v-else-if="filteredCourses.length === 0" class="empty-state">
				<div class="empty-icon">📅</div>
				<h3 v-if="includePast">Нет прошедших конференций</h3>
				<h3 v-else>Нет запланированных конференций</h3>
				<p v-if="includePast">Завершённые конференции появятся здесь</p>
				<p v-else>Создайте первую конференцию в одном из ваших курсов</p>
				<button v-if="!includePast" @click="showCourseSelector = true" class="create-first-btn">
					Создать конференцию
				</button>
			</div>

			<!-- 🔥 ГОРИЗОНТАЛЬНЫЙ СЛАЙДЕР КАРТОЧЕК -->
			<div v-else class="cards-slider-wrapper">
				<div class="cards-slider">
					<div class="cards-track" :style="trackStyle">
						<article
							v-for="(courseGroup, index) in filteredCourses"
							:key="courseGroup.course_id"
							class="dash-conf-card"
							:style="{ '--accent': getColor(index, courseGroup.course_id) }"
						>
							<!-- Заголовок карточки -->
							<header class="card-header">
								<div class="header-row">
									<span class="color-bar"></span>
									<h3 class="card-title">{{ courseGroup.course_title }}</h3>
								</div>
								<span v-if="courseGroup.course_category" class="card-category">
									{{ translateCategory(courseGroup.course_category) }}
								</span>
							</header>

							<!-- Список дат -->
							<div
								class="dates-list"
								:class="{ scrollable: courseGroup.conferences.length > 3 }"
							>
								<div
									v-for="conference in getSortedConferences(
										courseGroup.conferences
									)"
									:key="conference.id"
									class="date-row"
									:class="getRowClass(conference)"
								>
									<span
										class="status-dot"
										:style="{
											background: getDotColor(
												conference,
												index,
												courseGroup.course_id
											),
										}"
										:class="getDotClass(conference)"
									></span>

									<div class="time-block">
										<time class="time">{{
											formatTime(conference.scheduled_at)
										}}</time>
										<span class="date">{{
											formatDateShort(conference.scheduled_at)
										}}</span>
									</div>

									<span class="conf-name" :title="conference.title">
										{{ conference.title }}
									</span>

									<button
										v-if="shouldShowJoinButton(conference)"
										class="btn-action"
										:class="getStatusLabelClass(conference)"
										@click="joinConference(conference)"
									>
										{{ getStatusLabel(conference) }}
									</button>
									<span
										v-else
										class="btn-status"
										:class="getStatusLabelClass(conference)"
									>
										{{ getStatusLabel(conference) }}
									</span>

									<!-- 🔥 Счётчик участников (показываем если есть участники или конференция сегодня/live) -->
									<span
									v-if="getParticipantCount(conference) > 0 || isLiveNow(conference)"
										class="participants-text"
									>
										Участников: {{ getParticipantCount(conference) }}
										<span class="duration-timer">
											({{ getConferenceDuration(conference) || '00:00:00' }})
										</span>
									</span>
								</div>
							</div>

							<!-- Футер карточки -->
							<footer class="card-footer">
								<span class="conf-count">
									{{ courseGroup.conferences.length }}
									{{ pluralize(courseGroup.conferences.length) }}
								</span>
								<router-link
									:to="`/teacher/courses/${courseGroup.course_id}/edit?tab=conferences`"
									class="manage-link"
								>
									Управление →
								</router-link>
							</footer>
						</article>
					</div>
				</div>
			</div>

			<!-- Точки навигации -->
			<div class="dots-nav" v-if="showSliderNav">
				<button
					v-for="i in maxSlide + 1"
					:key="i"
					class="dot"
					:class="{ active: currentSlide === i - 1 }"
					@click="goToSlide(i - 1)"
				></button>
			</div>
		</div>

		<!-- Модалка выбора курса для создания конференции -->
		<Teleport to="body">
			<div
				v-if="showCourseSelector"
				class="modal-overlay"
				@click.self="showCourseSelector = false"
			>
				<div class="modal-content course-selector">
					<header class="modal-header">
						<h3>Выберите курс</h3>
						<button class="close-btn" @click="showCourseSelector = false">
							×
						</button>
					</header>

					<div class="courses-list">
						<button
							v-for="course in teacherCourses"
							:key="course.id"
							class="course-option"
							@click="goToCreateConference(course.id)"
						>
							<span class="course-name">{{ course.title }}</span>
							<span class="course-conferences-count">
								{{ getConferencesCount(course.id) }} конференций
							</span>
						</button>
					</div>

					<div v-if="teacherCourses.length === 0" class="no-courses">
						<p>У вас пока нет курсов</p>
						<router-link
							to="/teacher/courses/create"
							class="create-course-link"
						>
							Создать курс
						</router-link>
					</div>
				</div>
			</div>
		</Teleport>

		<!-- Модалка подтверждения удаления -->
		<Teleport to="body">
			<div
				v-if="conferenceToDelete"
				class="modal-overlay"
				@click.self="conferenceToDelete = null"
			>
				<div class="modal-content confirm-delete">
					<header class="modal-header">
						<h3>Удалить конференцию?</h3>
					</header>

					<div class="modal-body">
						<p>
							Вы уверены, что хотите удалить конференцию
							<strong>"{{ conferenceToDelete.title }}"</strong>?
						</p>
						<p class="warning">Это действие нельзя отменить.</p>
					</div>

					<footer class="modal-footer">
						<button class="btn-cancel" @click="conferenceToDelete = null">
							Отмена
						</button>
						<button
							class="btn-delete"
							@click="deleteConference"
							:disabled="deleting"
						>
							{{ deleting ? 'Удаление...' : 'Удалить' }}
						</button>
					</footer>
				</div>
			</div>
		</Teleport>
	</div>
</template>

<script>
import coursesService from '@/services/courses'
import conferencesService from '@/services/conferences'
import { mapActions, mapState } from 'pinia'
import { useConferencesStore } from '@/stores/conferences'
import { io } from 'socket.io-client'

// WebSocket для live счётчика участников
let dashboardSocket = null

// 🔥 Палитра цветов для курсов
const COLORS = [
	'#0096ed', // фиолетовый
	'#00B894', // изумрудный
	'#E17055', // коралловый
	'#0984E3', // синий
	'#FDCB6E', // жёлтый
	'#E84393', // розовый
	'#00CEC9', // бирюзовый
	'#FF7675', // красный
	'#74B9FF', // голубой
	'#0096ed', // лавандовый
]

// 🔥 Словарь переводов категорий
const JOIN_WINDOW_MINUTES = 30
const SOON_WINDOW_MINUTES = 60

const CATEGORY_TRANSLATIONS = {
	// Технические
	tech: 'Технологии',
	technology: 'Технологии',
	programming: 'Программирование',
	development: 'Разработка',
	web: 'Веб-разработка',
	mobile: 'Мобильная разработка',
	devops: 'DevOps',

	// Бизнес
	business: 'Бизнес',
	sales: 'Продажи',
	marketing: 'Маркетинг',
	management: 'Менеджмент',
	finance: 'Финансы',
	hr: 'HR',

	// Дизайн
	design: 'Дизайн',
	ux: 'UX/UI',
	graphics: 'Графический дизайн',

	// Образование
	education: 'Образование',
	language: 'Языки',
	soft_skills: 'Soft Skills',

	// Прочее
	other: 'Другое',
	general: 'Общее',
}

export default {
	name: 'ConferencesSection',

	emits: ['conferences-loaded'],

	data() {
		return {
			teacherCourses: [],
			coursesLoading: false,
			coursesError: null,

			selectedCategory: null,
			showCategoryDropdown: false,
			includePast: false,
			showCourseSelector: false,

			conferenceToDelete: null,
			deleting: false,
			fallbackConferences: [],

			isUsingFallback: false,

			// 🔥 Слайдер — динамические параметры
			currentSlide: 0,
			cardWidth: 380, // 360px карточка + 20px gap
			visibleCards: 3,
			showLiveOnly: false,
			
			// Live счётчик участников { roomId: count }
			participantCounts: {},
			
			// Время начала конференций { conferenceId: started_at }
			conferenceStartTimes: {},
			
			// Накопленная длительность { conferenceId: seconds }
			conferenceAccumulatedDurations: {},
			
			// Таймер для обновления длительности
			durationTimer: null,
			
			// Текущее время для реактивного обновления
			currentTime: Date.now(),
		}
	},

	computed: {
		...mapState(useConferencesStore, {
			storeConferences: state => state.teacherConferences,
			storeLoadingTeacher: state => state.loading.teacher,
			storeErrorTeacher: state => state.errors.teacher,
		}),

		availableCategories() {
			const categories = new Set()

			this.coursesWithConferences.forEach(course => {
				if (course.course_category) {
					categories.add(course.course_category)
				}
			})

			// 🔥 Возвращаем объекты с value и label
			return Array.from(categories)
				.map(cat => ({
					value: cat,
					label: this.translateCategory(cat),
				}))
				.sort((a, b) => a.label.localeCompare(b.label, 'ru'))
		},

		selectedCategoryLabel() {
			if (!this.selectedCategory) return 'Все категории'
			return this.translateCategory(this.selectedCategory)
		},

		filteredCourses() {
			let courses = this.coursesWithConferences
			const now = Date.now()

			// Хелперы для проверки статуса
			const isLiveNow = conf => this.isLiveNow(conf)

			const isEnded = conf => this.isEnded(conf)

			// Фильтр по категории
			if (this.selectedCategory) {
				courses = courses.filter(
					course => course.course_category === this.selectedCategory
				)
			}

			// Фильтр по статусу (прошедшие / актуальные)
			if (this.includePast) {
				// Показать ТОЛЬКО завершённые
				courses = courses
					.map(course => ({
						...course,
						conferences: course.conferences.filter(conf => isEnded(conf)),
					}))
					.filter(course => course.conferences.length > 0)
			} else {
				// Показать live + будущие
				courses = courses
					.map(course => ({
						...course,
						conferences: course.conferences.filter(conf => {
							if (isLiveNow(conf)) return true
							if (this.canJoinSoon(conf)) return true
							if (isEnded(conf)) return false
							// Будущие
							const scheduled = this.parseLocalDate(conf.scheduled_at).getTime()
							return scheduled > now
						}),
					}))
					.filter(course => course.conferences.length > 0)
			}

			// 🔥 Фильтр "только в эфире"
			if (this.showLiveOnly) {
				courses = courses
					.map(course => ({
						...course,
						conferences: course.conferences.filter(conf => isLiveNow(conf)),
					}))
					.filter(course => course.conferences.length > 0)
			}

			return courses
		},

		coursesWithConferences() {
			if (
				Array.isArray(this.storeConferences) &&
				this.storeConferences.length > 0
			) {
				return this.storeConferences
			}
			if (
				Array.isArray(this.fallbackConferences) &&
				this.fallbackConferences.length > 0
			) {
				return this.fallbackConferences
			}
			return []
		},

		loading() {
			return this.storeLoadingTeacher || this.coursesLoading
		},

		error() {
			if (this.coursesWithConferences.length > 0) {
				return null
			}
			return this.coursesError || this.storeErrorTeacher
		},

		// 🔥 Слайдер: показывать навигацию?
		showSliderNav() {
			return this.filteredCourses.length > this.visibleCards
		},

		// 🔥 Слайдер: максимальный индекс
		maxSlide() {
			const max = this.filteredCourses.length - this.visibleCards
			return Math.max(0, max)
		},

		// 🔥 Слайдер: стиль трека
		trackStyle() {
			return {
				transform: `translateX(-${this.currentSlide * this.cardWidth}px)`,
			}
		},

		// 🔥 Все конференции с цветами (для календаря)
		allConferencesWithColors() {
			const result = []
			this.filteredCourses.forEach((group, index) => {
				const color = this.getColor(index, group.course_id)
				if (group.conferences) {
					group.conferences.forEach(conf => {
						result.push({
							...conf,
							color,
							courseId: group.course_id,
							courseTitle: group.course_title,
						})
					})
				}
			})
			return result
		},
	},

	methods: {
		...mapActions(useConferencesStore, {
			fetchTeacherConferencesFromStore: 'fetchTeacherConferences',
			startConferenceFromStore: 'startConference',
			deleteConferenceFromStore: 'deleteConference',
			applyConferenceUpdateFromStore: 'applyConferenceUpdate',
		}),
		// 🔥 Перевод категории
		translateCategory(category) {
			if (!category) return 'Без категории'

			const key = category.toLowerCase().trim()
			return CATEGORY_TRANSLATIONS[key] || category
		},

		/**
		 * Парсинг даты как локального времени
		 * scheduled_at приходит без таймзоны (2025-12-21T02:16:00)
		 */
		parseLocalDate(dateStr) {
			if (!dateStr) return new Date(NaN)
			if (/[Z+-]\d{2}:?\d{2}$/.test(dateStr) || dateStr.endsWith('Z')) {
				return new Date(dateStr)
			}
			const [datePart, timePart] = dateStr.split('T')
			if (!datePart) return new Date(NaN)
			const [year, month, day] = datePart.split('-').map(Number)
			const [hour = 0, minute = 0, second = 0] = (timePart || '00:00:00')
				.split(':')
				.map(Number)
			return new Date(year, month - 1, day, hour, minute, second)
		},

		// 🔥 Получить цвет курса (детерминированный по ID)
		getColor(index, courseId) {
			if (courseId) {
				const hash = String(courseId)
					.split('')
					.reduce((a, c) => a + c.charCodeAt(0), 0)
				return COLORS[hash % COLORS.length]
			}
			return COLORS[index % COLORS.length]
		},

		// 🔥 Сортировка конференций по дате
		getSortedConferences(conferences) {
			return [...conferences].sort((a, b) => {
				// Приоритет статусов: live → скоро → запланированные → прошедшие/отменённые
				const getPriority = conf => {
					if (this.isLiveNow(conf)) return 0
					if (this.canJoinSoon(conf)) return 1
					if (this.isStartingSoon(conf)) return 2
					if (conf.status === 'cancelled') return 5
					if (this.isEnded(conf)) return 4
					// Запланированные в будущем
					const scheduled = new Date(conf.scheduled_at).getTime()
					if (scheduled > Date.now()) return 3
					return 4 // Прошедшие без статуса
				}

				const priorityA = getPriority(a)
				const priorityB = getPriority(b)

				if (priorityA !== priorityB) {
					return priorityA - priorityB
				}

				// Внутри одного приоритета — по дате
				// Для live/скоро — ближайшие первыми
				// Для прошедших — недавние первыми (desc)
				if (priorityA <= 3) {
					return new Date(a.scheduled_at) - new Date(b.scheduled_at)
				}
				return new Date(b.scheduled_at) - new Date(a.scheduled_at)
			})
		},

		// 🔥 Проверка: идёт ли сейчас
		isLiveNow(conf) {
			if (
				conf.status === 'ended' ||
				conf.status === 'completed' ||
				conf.status === 'cancelled' ||
				conf.ended_at
			) {
				return false
			}

			if (conf.status === 'live' || conf.status === 'in_progress') return true

			const conferenceId = String(conf.id)
			const startedAt =
				this.conferenceStartTimes[conferenceId] || conf.started_at
			return Boolean(startedAt)
		},

		isEnded(conf) {
			if (
				conf.status === 'ended' ||
				conf.status === 'completed' ||
				conf.status === 'cancelled' ||
				conf.ended_at
			) {
				return true
			}

			if (this.isLiveNow(conf)) return false

			const now = Date.now()
			const scheduled = new Date(conf.scheduled_at).getTime()
			const duration = (conf.duration_minutes || 60) * 60 * 1000

			return now > scheduled + duration
		},

		// 🔥 Проверка: скоро начнётся (в течение часа)
		isStartingSoon(conf) {
			if (this.isLiveNow(conf)) return false
			if (this.isEnded(conf)) return false
			if (conf.status === 'cancelled') return false

			const diff = new Date(conf.scheduled_at).getTime() - Date.now()
			const soonMs = SOON_WINDOW_MINUTES * 60 * 1000
			const joinMs = JOIN_WINDOW_MINUTES * 60 * 1000
			return diff <= soonMs && diff > joinMs
		},

		// 🔥 Проверка: конференция сегодня
		isTodayConference(conf) {
			const confDate = new Date(conf.scheduled_at)
			const today = new Date()
			return (
				confDate.getFullYear() === today.getFullYear() &&
				confDate.getMonth() === today.getMonth() &&
				confDate.getDate() === today.getDate()
			)
		},

		// 🔥 Проверка: можно присоединиться (за 30 минут)
		canJoinSoon(conf) {
			if (this.isLiveNow(conf)) return false
			if (this.isEnded(conf)) return false
			if (conf.status === 'cancelled') return false

			const now = Date.now()
			const scheduled = new Date(conf.scheduled_at).getTime()
			const duration = (conf.duration_minutes || 60) * 60 * 1000
			const joinStart = scheduled - JOIN_WINDOW_MINUTES * 60 * 1000
			const joinEnd = scheduled + duration
			return now >= joinStart && now <= joinEnd
		},

		// 🔥 Класс для строки
		getRowClass(conf) {
			return {
				'is-live': this.isLiveNow(conf),
				'is-cancelled': conf.status === 'cancelled',
			}
		},

		// 🔥 Цвет точки
		getDotColor(conf, index, courseId) {
			if (this.isLiveNow(conf)) return '#27ae60' // зелёный
			if (conf.status === 'cancelled') return '#95a5a6' // серый
			if (this.isEnded(conf)) return '#95a5a6' // серый
			if (this.isStartingSoon(conf)) return '#f39c12' // оранжевый
			return this.getColor(index, courseId) // цвет курса
		},

		// 🔥 Класс для точки
		getDotClass(conf) {
			if (this.isLiveNow(conf)) return 'pulse'
			if (conf.status === 'cancelled' || this.isEnded(conf)) return 'gray'
			if (this.isStartingSoon(conf)) return 'orange'
			return ''
		},

		// 🔥 Показывать кнопку "Присоединиться"?
		shouldShowJoinButton(conf) {
			if (this.isLiveNow(conf)) return true
			if (conf.status === 'cancelled' || this.isEnded(conf)) return false
			return this.canJoinSoon(conf)
		},

		// 🔥 Класс для лейбла статуса
		getStatusLabelClass(conf) {
			if (this.isLiveNow(conf)) return 'live'
			if (conf.status === 'cancelled') return 'cancelled'
			if (this.isEnded(conf)) return 'ended'
			if (this.canJoinSoon(conf)) return 'join'
			if (this.isStartingSoon(conf)) return 'soon'
			return 'scheduled'
		},

		// 🔥 Текст статуса
		getStatusLabel(conf) {
			if (this.isLiveNow(conf)) return 'В эфире'
			if (conf.status === 'cancelled') return 'Отменено'
			if (this.isEnded(conf)) return 'Завершено'
			if (this.canJoinSoon(conf)) return 'Войти'
			if (this.isStartingSoon(conf)) return 'Скоро'
			return 'Запланировано'
		},

		// 🔥 Склонение слова "конференция"
		pluralize(n) {
			const abs = Math.abs(n) % 100
			const n1 = abs % 10
			if (abs > 10 && abs < 20) return 'конференций'
			if (n1 > 1 && n1 < 5) return 'конференции'
			if (n1 === 1) return 'конференция'
			return 'конференций'
		},

		// 🔥 Слайдер: следующий
		slideNext() {
			if (this.currentSlide < this.maxSlide) {
				this.currentSlide++
			}
		},

		// 🔥 Слайдер: предыдущий
		slidePrev() {
			if (this.currentSlide > 0) {
				this.currentSlide--
			}
		},

		// 🔥 Слайдер: перейти к индексу
		goToSlide(index) {
			this.currentSlide = Math.max(0, Math.min(index, this.maxSlide))
		},

		// 🔥 Обновление количества видимых карточек
		updateVisibleCards() {
			const width = window.innerWidth

			if (width >= 1700) {
				this.visibleCards = 4
				this.cardWidth = 380
			} else if (width >= 1300) {
				this.visibleCards = 3
				this.cardWidth = 380
			} else if (width >= 900) {
				this.visibleCards = 2
				this.cardWidth = 380
			} else {
				this.visibleCards = 1
				this.cardWidth = 360
			}

			// Корректируем текущий слайд
			if (this.currentSlide > this.maxSlide) {
				this.currentSlide = Math.max(0, this.maxSlide)
			}
		},

		// 🔥 Эмит данных для календаря
		emitConferencesData() {
			this.$emit('conferences-loaded', this.allConferencesWithColors)
		},

		async loadConferences() {
			this.coursesError = null
			this.coursesLoading = true
			this.isUsingFallback = false

			try {
				const results = await Promise.allSettled([
					this.fetchTeacherConferencesFromStore({
						includePast: this.includePast,
						force: true,
					}),
					this.loadTeacherCourses(),
				])

				const [conferencesResult, coursesResult] = results

				console.log('📊 Load results:', {
					conferences: conferencesResult.status,
					courses: coursesResult.status,
				})

				if (coursesResult.status === 'rejected') {
					console.error('❌ Courses failed:', coursesResult.reason)
					this.coursesError = 'Не удалось загрузить курсы'
					return
				}

				if (conferencesResult.status === 'fulfilled') {
					console.log('✅ Conferences loaded from store')
					this.fallbackConferences = []
					this.emitConferencesData()
					return
				}

				console.warn('⚠️ Store conferences failed, trying fallback...')
				console.warn('Reason:', conferencesResult.reason)

				const fallback = await this.buildFallbackConferences()

				if (fallback.length > 0) {
					this.fallbackConferences = fallback
					this.isUsingFallback = true
					this.coursesError = null
					console.log(
						'✅ Fallback loaded:',
						fallback.length,
						'courses with conferences'
					)
				} else {
					this.fallbackConferences = []
					this.coursesError = null
					console.log('ℹ️ No conferences found (fallback empty)')
				}

				this.emitConferencesData()
			} catch (err) {
				console.error('❌ Critical error in loadConferences:', err)
				this.coursesError =
					'Не удалось загрузить конференции. Попробуйте позже.'
			} finally {
				this.coursesLoading = false
			}
		},

		async buildFallbackConferences() {
			console.log('🔄 Building fallback conferences...')

			if (!this.teacherCourses.length) {
				try {
					const raw = await coursesService.getTeacherCourses()
					this.teacherCourses = this.normalizeCoursesResponse(raw)
					console.log(
						'📚 Courses loaded for fallback:',
						this.teacherCourses.length
					)
				} catch (err) {
					console.error('❌ Failed to load courses for fallback:', err)
					return []
				}
			}

			const courses = Array.isArray(this.teacherCourses)
				? this.teacherCourses
				: []

			if (courses.length === 0) {
				console.log('ℹ️ No courses available')
				return []
			}

			const result = []

			const conferencePromises = courses.map(async course => {
				try {
					const conferences = await conferencesService.getByCourseId(
						course.id,
						this.includePast,
						true
					)

					const normalizedConferences =
						this.normalizeConferencesResponse(conferences)

					console.log(
						`📅 Course ${course.id} (${course.title}):`,
						normalizedConferences.length,
						'conferences'
					)

					return {
						course_id: course.id,
						course_title: course.title,
						course_category: course.category || course.category_name || '',
						conferences: normalizedConferences,
					}
				} catch (err) {
					console.warn(
						`⚠️ Failed to load conferences for course ${course.id}:`,
						err.message
					)
					return {
						course_id: course.id,
						course_title: course.title,
						course_category: course.category || course.category_name || '',
						conferences: [],
					}
				}
			})

			const results = await Promise.allSettled(conferencePromises)

			for (const res of results) {
				if (res.status === 'fulfilled' && res.value) {
					if (res.value.conferences.length > 0) {
						result.push(res.value)
					}
				}
			}

			console.log(
				'✅ Fallback built:',
				result.length,
				'courses with conferences'
			)
			return result
		},

		normalizeConferencesResponse(data) {
			if (Array.isArray(data)) return data
			if (Array.isArray(data?.data)) return data.data
			if (Array.isArray(data?.conferences)) return data.conferences
			return []
		},

		async loadTeacherCourses() {
			try {
				const raw = await coursesService.getTeacherCourses()
				this.teacherCourses = this.normalizeCoursesResponse(raw)
				console.log('✅ Teacher courses loaded:', this.teacherCourses.length)
			} catch (err) {
				console.error('❌ Failed to load teacher courses:', err)
				this.teacherCourses = []
				throw err
			}
		},

		normalizeCoursesResponse(raw) {
			if (Array.isArray(raw)) return raw
			if (Array.isArray(raw?.data)) return raw.data
			if (Array.isArray(raw?.data?.courses)) return raw.data.courses
			if (Array.isArray(raw?.courses)) return raw.courses
			return []
		},

		getConferencesCount(courseId) {
			const course = this.coursesWithConferences.find(
				c => c.course_id === courseId
			)
			return course ? course.conferences.length : 0
		},

		findConferenceById(conferenceId) {
			const targetId = String(conferenceId)
			for (const group of this.coursesWithConferences) {
				const conf = (group.conferences || []).find(
					item => String(item.id) === targetId
				)
				if (conf) return conf
			}
			return null
		},

		/** Найти конференцию по roomId (webrtc_channel) */
		findConferenceByRoomId(roomId) {
			for (const group of this.coursesWithConferences) {
				const conf = (group.conferences || []).find(
					item => item.webrtc_channel === roomId || `conference_${item.id}` === roomId
				)
				if (conf) return conf
			}
			return null
		},

		applyConferenceUpdateLocal(conferenceId, patch) {
			if (!conferenceId) return

			if (typeof this.applyConferenceUpdateFromStore === 'function') {
				this.applyConferenceUpdateFromStore(conferenceId, patch)
			}

			if (Array.isArray(this.fallbackConferences)) {
				const targetId = String(conferenceId)
				this.fallbackConferences = this.fallbackConferences.map(group => ({
					...group,
					conferences: (group.conferences || []).map(conf =>
						String(conf.id) === targetId ? { ...conf, ...patch } : conf
					),
				}))
			}
		},

		freezeConferenceTimer(conferenceId) {
			const key = String(conferenceId)
			const hasLiveStart = Object.prototype.hasOwnProperty.call(
				this.conferenceStartTimes,
				key
			)
			const conf = this.findConferenceById(conferenceId)
			const startedAt = hasLiveStart
				? this.conferenceStartTimes[key]
				: conf?.started_at
			const accumulatedSeconds = Object.prototype.hasOwnProperty.call(
				this.conferenceAccumulatedDurations,
				key
			)
				? this.conferenceAccumulatedDurations[key]
				: conf?.accumulated_duration || 0

			if (startedAt) {
				const startTime = new Date(startedAt).getTime()
				if (!Number.isNaN(startTime)) {
					const now = Date.now()
					const currentSessionSeconds = Math.max(
						0,
						Math.floor((now - startTime) / 1000)
					)
					const totalSeconds = accumulatedSeconds + currentSessionSeconds
					this.conferenceAccumulatedDurations = {
						...this.conferenceAccumulatedDurations,
						[key]: totalSeconds,
					}
				}
			}

			this.conferenceStartTimes = {
				...this.conferenceStartTimes,
				[key]: null,
			}
		},

		formatTime(dateStr) {
			if (!dateStr) return '--:--'
			const date = new Date(dateStr)
			return date.toLocaleTimeString('ru-RU', {
				hour: '2-digit',
				minute: '2-digit',
			})
		},

		// 🔥 Короткий формат даты для карточки
		formatDateShort(dateStr) {
			if (!dateStr) return ''
			const date = new Date(dateStr)
			const today = new Date()
			const tomorrow = new Date(today)
			tomorrow.setDate(tomorrow.getDate() + 1)

			if (date.toDateString() === today.toDateString()) return 'Сегодня'
			if (date.toDateString() === tomorrow.toDateString()) return 'Завтра'

			return date.toLocaleDateString('ru-RU', {
				day: 'numeric',
				month: 'short',
				weekday: 'short',
			})
		},

		joinConference(conference) {
			this.$router.push(`/conference/${conference.id}`)
		},

		goToCreateConference(courseId) {
			this.showCourseSelector = false
			this.$router.push(
				`/teacher/courses/${courseId}/edit?tab=conferences&action=create`
			)
		},

		toggleCategoryDropdown() {
			this.showCategoryDropdown = !this.showCategoryDropdown
		},

		closeCategoryDropdown() {
			this.showCategoryDropdown = false
		},

		selectCategory(category) {
			this.selectedCategory = category
			this.showCategoryDropdown = false
			this.currentSlide = 0 // 🔥 Сброс позиции слайдера
		},

		/** Переключение прошедших */
		onTogglePast() {
			// Сбрасываем "В эфире" при выборе прошедших
			if (this.includePast) {
				this.showLiveOnly = false
			}
			this.loadConferences()
		},

		/** Переключение "В эфире" */
		onToggleLive() {
			// Сбрасываем "Показать прошедшие" при выборе "В эфире"
			if (this.showLiveOnly && this.includePast) {
				this.includePast = false
				this.loadConferences()
			}
		},

		async deleteConference() {
			if (!this.conferenceToDelete) return

			this.deleting = true

			try {
				await this.deleteConferenceFromStore(
					this.conferenceToDelete.id,
					this.conferenceToDelete.course_id
				)

				if (this.isUsingFallback) {
					const courseGroup = this.fallbackConferences.find(
						c => c.course_id === this.conferenceToDelete.course_id
					)
					if (courseGroup) {
						courseGroup.conferences = courseGroup.conferences.filter(
							c => c.id !== this.conferenceToDelete.id
						)
					}
				}

				this.$toast?.success('Конференция удалена')
				this.conferenceToDelete = null
				this.emitConferencesData()
			} catch (err) {
				console.error('Failed to delete conference:', err)
				this.$toast?.error('Не удалось удалить конференцию')
			} finally {
				this.deleting = false
			}
		},

		// ==================== LIVE SOCKET ====================

		/** Инициализация WebSocket для live счётчика */
		initLiveSocket() {
			if (dashboardSocket) return

			// В production или на удалённом сервере используем текущий origin
			const wsUrl = (process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost')
				? window.location.origin
				: (import.meta.env.VITE_WS_URL || 'http://localhost:3003')
			
			console.log('[Dashboard] Подключение к WebSocket:', `${wsUrl}/webrtc`)
			
			dashboardSocket = io(`${wsUrl}/webrtc`, {
				transports: ['websocket'],
				autoConnect: true,
			})

			dashboardSocket.on('connect', () => {
				console.log('[Dashboard] WebSocket подключен, socket.id:', dashboardSocket.id)
				// Подписываемся после подключения
				this.subscribeLiveConferences()
			})

			dashboardSocket.on('disconnect', () => {
				console.log('[Dashboard] WebSocket отключен')
			})

			dashboardSocket.on('participant-count-updated', ({ roomId, count }) => {
				console.log('[Dashboard] Получено обновление участников:', { roomId, count })
				this.participantCounts = {
					...this.participantCounts,
					[roomId]: count,
				}
			})

			// Обработчик начала/паузы конференции
			dashboardSocket.on('conference-started', ({ roomId, conferenceId, started_at, accumulated_duration, paused }) => {
				console.log('[Dashboard] Конференция событие:', { roomId, conferenceId, started_at, accumulated_duration, paused })
				// Используем conferenceId из события, или извлекаем из roomId
				let confId = conferenceId
				if (!confId) {
					const match = roomId.match(/conference_(\d+)/) || roomId.match(/^(\d+)$/)
					if (match) confId = match[1]
				}
				if (confId) {
					const key = String(confId)
					if (paused) {
						// На паузе - сохраняем накопленное время и убираем started_at
						this.conferenceStartTimes = {
							...this.conferenceStartTimes,
							[key]: null,
						}
						this.conferenceAccumulatedDurations = {
							...this.conferenceAccumulatedDurations,
							[key]: accumulated_duration || 0,
						}
					} else {
						// Активна - сохраняем started_at и accumulated_duration
						this.conferenceStartTimes = {
							...this.conferenceStartTimes,
							[key]: started_at,
						}
						if (accumulated_duration !== undefined) {
							this.conferenceAccumulatedDurations = {
								...this.conferenceAccumulatedDurations,
								[key]: accumulated_duration,
							}
						}
					}
				}
			})

			// Обработчик завершения конференции (для мгновенного обновления статуса)
			dashboardSocket.on('conference-ended', ({ roomId, conferenceId, ended_at, started_at }) => {
				console.log('[Dashboard] 🔴 Конференция завершена:', { roomId, conferenceId, ended_at, started_at })
				let confId = conferenceId
				if (!confId && roomId) {
					// Пытаемся найти конференцию по webrtc_channel
					const foundConf = this.findConferenceByRoomId(roomId)
					if (foundConf) {
						confId = foundConf.id
						console.log('[Dashboard] Найдена конференция по roomId:', confId)
					} else {
						// Fallback на старый regex (для обратной совместимости)
						const match =
							roomId.match(/conference_(\d+)/) ||
							roomId.match(/^(\d+)$/)
						if (match) confId = match[1]
					}
				}
				if (!confId) {
					console.warn('[Dashboard] Не удалось определить conferenceId из события:', { roomId, conferenceId })
					return
				}

				console.log('[Dashboard] Обновляем статус конференции:', confId, '-> completed')
				const patch = {
					status: 'completed',
				}
				if (ended_at) patch.ended_at = ended_at
				if (started_at) patch.started_at = started_at

				this.applyConferenceUpdateLocal(confId, patch)
				this.freezeConferenceTimer(confId)

				if (roomId) {
					this.participantCounts = {
						...this.participantCounts,
						[roomId]: 0,
					}
				}

				this.emitConferencesData()
			})
		},

		/** Отключение WebSocket */
		disconnectLiveSocket() {
			if (dashboardSocket) {
				dashboardSocket.disconnect()
				dashboardSocket = null
			}
		},

		/** Подписка на конференции для отслеживания участников */
		subscribeLiveConferences() {
			// Проверяем что сокет существует И подключен
			if (!dashboardSocket || !dashboardSocket.connected) {
				console.log('[Dashboard] Сокет не подключен, пропускаем подписку')
				return
			}

			// Собираем все roomId для конференций (подписываемся на все, не только live/today)
			const roomIds = []
			this.coursesWithConferences.forEach(course => {
				course.conferences?.forEach(conf => {
					const isLive = this.isLiveNow(conf)
					const isToday = this.isTodayConference(conf)
					console.log('[Dashboard] Конференция:', {
						id: conf.id,
						title: conf.title,
						status: conf.status,
						scheduled_at: conf.scheduled_at,
						isLive,
						isToday,
						webrtc_channel: conf.webrtc_channel,
					})

					// Используем webrtc_channel как roomId для WebRTC
					const roomId = conf.webrtc_channel || `conference_${conf.id}`
					// Подписываемся на все конференции для отслеживания участников
					roomIds.push(roomId)
					dashboardSocket.emit('subscribe-conference', { roomId })
				})
			})

			console.log('[Dashboard] Подписка на конференции:', roomIds)
		},

		/** Получить счётчик участников для конференции */
		getParticipantCount(conf) {
			const roomId = conf.webrtc_channel || `conference_${conf.id}`
			return this.participantCounts[roomId] || 0
		},

		/** Получить длительность конференции в формате ЧЧ:ММ:СС */
		getConferenceDuration(conf) {
			// Используем currentTime для реактивности
			const _ = this.currentTime
			
			const conferenceId = String(conf.id)
			const hasLiveStart = Object.prototype.hasOwnProperty.call(
				this.conferenceStartTimes,
				conferenceId
			)
			const startedAt = hasLiveStart
				? this.conferenceStartTimes[conferenceId]
				: conf.started_at
			const accumulatedSeconds = Object.prototype.hasOwnProperty.call(
				this.conferenceAccumulatedDurations,
				conferenceId
			)
				? this.conferenceAccumulatedDurations[conferenceId]
				: 0
			
			// Если на паузе (startedAt = null) - показываем только накопленное время
			if (!startedAt) {
				if (accumulatedSeconds > 0) {
					return this.formatDurationSeconds(accumulatedSeconds)
				}
				return null
			}
			
			const startTime = new Date(startedAt).getTime()
			const now = Date.now()
			const currentSessionSeconds = Math.floor((now - startTime) / 1000)
			const totalSeconds = accumulatedSeconds + currentSessionSeconds
			
			if (totalSeconds < 0) return null
			
			return this.formatDurationSeconds(totalSeconds)
		},

		/** Форматирование секунд в ЧЧ:ММ:СС */
		formatDurationSeconds(totalSeconds) {
			const hours = Math.floor(totalSeconds / 3600)
			const minutes = Math.floor((totalSeconds % 3600) / 60)
			const seconds = totalSeconds % 60
			
			const pad = n => String(n).padStart(2, '0')
			
			if (hours > 0) {
				return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
			}
			return `${pad(minutes)}:${pad(seconds)}`
		},
	},

	watch: {
		// 🔥 Сбрасываем позицию и эмитим данные при изменении фильтра
		filteredCourses() {
			this.currentSlide = 0
			this.emitConferencesData()
		},
		
		// Переподписываемся на live конференции при изменении данных
		coursesWithConferences: {
			handler() {
				this.subscribeLiveConferences()
			},
			deep: true,
		},
	},

	mounted() {
		this.loadConferences()
		this.updateVisibleCards()
		window.addEventListener('resize', this.updateVisibleCards)
		this.initLiveSocket()
		
		// Таймер для обновления длительности каждую секунду
		this.durationTimer = setInterval(() => {
			this.currentTime = Date.now()
		}, 1000)
	},

	beforeUnmount() {
		window.removeEventListener('resize', this.updateVisibleCards)
		this.disconnectLiveSocket()
		
		// Очищаем таймер
		if (this.durationTimer) {
			clearInterval(this.durationTimer)
			this.durationTimer = null
		}
	},

	directives: {
		'click-outside': {
			mounted(el, binding) {
				el._clickOutside = event => {
					if (!(el === event.target || el.contains(event.target))) {
						binding.value()
					}
				}
				document.addEventListener('click', el._clickOutside)
			},
			unmounted(el) {
				document.removeEventListener('click', el._clickOutside)
			},
		},
	},
}
</script>

<style scoped>
/* ============================================
   ОСНОВНОЙ КОНТЕЙНЕР
   ============================================ */
.conferences-section {
	width: 100%;
	padding: 20px 0;
}

/* 🔥 Центрирующий контейнер */
.section-container {
	max-width: 1560px;
	margin: 0 auto;
	padding: 0 24px;
}

/* ============================================
   HEADER — ЦЕНТРИРОВАННЫЙ
   ============================================ */
.section-header {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 16px;
	margin-bottom: 32px;
	flex-wrap: wrap;
}

.section-title {
	font-size: 28px;
	font-weight: 600;
	margin: 0;
	color: #252641;
}
/* 🔥 Чекбоксы-переключатели */
.toggle-checkbox {
	display: flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	user-select: none;
}

.toggle-checkbox input {
	display: none;
}

.toggle-label {
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 10px 16px;
	background: #f5f5f5;
	border-radius: 10px;
	font-size: 14px;
	color: #555;
	transition: all 0.2s;
	border: 2px solid transparent;
}

.toggle-checkbox input:checked + .toggle-label {
	background: #eaf6ff;
	color: #0096ed;
	border-color: #0096ed;
}

.toggle-checkbox:hover .toggle-label {
	background: #ececec;
}

.toggle-checkbox input:checked + .toggle-label:hover {
	background: #e3f2fd;
}

/* 🔥 Специальный стиль для "В эфире" */
.toggle-label.live {
	gap: 8px;
}

.live-dot {
	width: 8px;
	height: 8px;
	background: #aaa;
	border-radius: 50%;
	transition: all 0.2s;
}

.toggle-checkbox input:checked + .toggle-label.live {
	background: #e8f5e9;
	color: #27ae60;
	border-color: #27ae60;
}

.toggle-checkbox input:checked + .toggle-label.live .live-dot {
	background: #27ae60;
	animation: pulse-dot 1.5s ease-in-out infinite;
}

@keyframes pulse-dot {
	0%,
	100% {
		transform: scale(1);
		opacity: 1;
	}
	50% {
		transform: scale(1.3);
		opacity: 0.7;
	}
}
/* 🔥 Группа контролов — горизонтально по центру */
.header-controls {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 16px;
	flex-wrap: wrap;
}

/* Фильтр категорий */
.category-filter {
	position: relative;
}

.filter-button {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 18px;
	border: 1px solid #e0e0e0;
	border-radius: 10px;
	background: white;
	cursor: pointer;
	transition: all 0.2s;
	font-size: 15px;
}

.filter-button:hover,
.filter-button.active {
	border-color: #0096ed;
	background: #f2f8ff;
}

.filter-icon {
	width: 16px;
	height: 16px;
}

.dropdown-arrow {
	font-size: 10px;
	transition: transform 0.2s;
	color: #888;
}

.dropdown-arrow.rotated {
	transform: rotate(180deg);
}

.dropdown-menu {
	position: absolute;
	top: calc(100% + 8px);
	left: 50%;
	transform: translateX(-50%);
	min-width: 200px;
	background: white;
	border: 1px solid #e0e0e0;
	border-radius: 12px;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
	z-index: 100;
	overflow: hidden;
}

.dropdown-item {
	display: block;
	width: 100%;
	padding: 12px 18px;
	text-align: left;
	border: none;
	background: none;
	cursor: pointer;
	transition: background 0.2s;
	font-size: 14px;
}

.dropdown-item:hover {
	background: #f5f5f5;
}

.dropdown-item.selected {
	background: #eaf6ff;
	color: #0096ed;
	font-weight: 500;
}

/* Переключатель прошедших */
.toggle-past {
	display: flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	user-select: none;
	font-size: 15px;
	color: #555;
	padding: 10px 16px;
	background: #f5f5f5;
	border-radius: 10px;
	transition: background 0.2s;
}

.toggle-past:hover {
	background: #ececec;
}

.toggle-past input {
	cursor: pointer;
	width: 16px;
	height: 16px;
}

/* 🔥 Навигация слайдера */
.slider-nav {
	display: flex;
	align-items: center;
	gap: 12px;
}

.nav-btn {
	width: 40px;
	height: 40px;
	border-radius: 50%;
	border: 2px solid #e0e0e0;
	background: #fff;
	font-size: 22px;
	cursor: pointer;
	transition: all 0.2s;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #666;
}

.nav-btn:hover:not(:disabled) {
	background: #0096ed;
	border-color: #0096ed;
	color: #fff;
}

.nav-btn:disabled {
	opacity: 0.4;
	cursor: not-allowed;
}

.slide-counter {
	font-size: 14px;
	color: #666;
	min-width: 60px;
	text-align: center;
	font-weight: 500;
}

/* Кнопка добавления */
.add-conference-btn {
	width: 44px;
	height: 44px;
	border-radius: 50%;
	border: 2px dashed #0096ed;
	background: transparent;
	color: #0096ed;
	font-size: 26px;
	cursor: pointer;
	transition: all 0.2s;
	display: flex;
	align-items: center;
	justify-content: center;
}

.add-conference-btn:hover {
	background: #0096ed;
	color: white;
	border-style: solid;
	transform: rotate(90deg);
}

/* ============================================
   СОСТОЯНИЯ
   ============================================ */
.loading-state,
.error-state,
.empty-state {
	text-align: center;
	padding: 80px 20px;
}

.spinner {
	width: 48px;
	height: 48px;
	border: 4px solid #f0f0f0;
	border-top-color: #0096ed;
	border-radius: 50%;
	animation: spin 1s linear infinite;
	margin: 0 auto 20px;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

.error-state {
	color: #e74c3c;
}

.retry-btn {
	margin-top: 16px;
	padding: 12px 32px;
	background: #0096ed;
	color: white;
	border: none;
	border-radius: 10px;
	cursor: pointer;
	font-size: 16px;
	transition: background 0.2s;
}

.retry-btn:hover {
	background: #0085d4;
}

.empty-state .empty-icon {
	font-size: 64px;
	margin-bottom: 20px;
}

.empty-state h3 {
	margin: 0 0 12px;
	color: #333;
	font-size: 22px;
}

.empty-state p {
	color: #666;
	margin: 0 0 28px;
	font-size: 16px;
}

.create-first-btn {
	padding: 14px 36px;
	background: #0096ed;
	color: white;
	border: none;
	border-radius: 10px;
	cursor: pointer;
	font-size: 17px;
	font-weight: 500;
	transition: all 0.2s;
}

.create-first-btn:hover {
	background: #0085d4;
	transform: translateY(-2px);
	box-shadow: 0 6px 20px rgba(0, 150, 237, 0.3);
}

/* ============================================
   🔥 СЛАЙДЕР КАРТОЧЕК — БЕЗ ОБРЕЗКИ
   ============================================ */
.cards-slider-wrapper {
	/* 🔥 Дополнительные отступы для теней */
	padding: 20px 0;
	margin: -20px 0;
}

.cards-slider {
	/* 🔥 overflow: visible чтобы карточки не обрезались */
	overflow: visible;
	/* 🔥 Но clip-path ограничивает только по бокам */
	clip-path: inset(-20px 0 -20px 0);
}

.cards-track {
	display: flex;
	gap: 20px;
	justify-content: center;
	transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
	/* 🔥 Padding для отображения теней */
	padding: 10px 0;
}

/* 🔥 Точки навигации */
.dots-nav {
	display: flex;
	justify-content: center;
	gap: 10px;
	margin-top: 28px;
}

.dot {
	width: 10px;
	height: 10px;
	border-radius: 50%;
	border: none;
	background: #ddd;
	cursor: pointer;
	padding: 0;
	transition: all 0.3s;
}

.dot:hover {
	background: #bbb;
	transform: scale(1.2);
}

.dot.active {
	background: #0096ed;
	width: 28px;
	border-radius: 5px;
}

/* ============================================
   🔥 КАРТОЧКА КОНФЕРЕНЦИИ
   ============================================ */
.dash-conf-card {
	width: 360px;
	flex-shrink: 0;
	background: #fff;
	border-radius: 20px;
	box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
	overflow: hidden;
	transition: transform 0.3s, box-shadow 0.3s;

	display: flex;
	flex-direction: column;
	min-height: 320px; /* минимальная высота */
}

.dash-conf-card:hover {
	transform: translateY(-6px);
	box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

/* Заголовок карточки */
.card-header {
	padding: 20px 24px;
	background: linear-gradient(
		135deg,
		var(--accent, #0096ed) 0%,
		color-mix(in srgb, var(--accent, #0096ed) 70%, white) 100%
	);
	color: #fff;
}

.header-row {
	display: flex;
	align-items: center;
	gap: 12px;
}

.color-bar {
	width: 4px;
	height: 28px;
	background: rgba(255, 255, 255, 0.5);
	border-radius: 2px;
}

.card-title {
	margin: 0;
	font-size: 17px;
	font-weight: 600;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.card-category {
	display: inline-block;
	margin-top: 10px;
	padding: 4px 12px;
	background: rgba(255, 255, 255, 0.2);
	border-radius: 14px;
	font-size: 12px;
	font-weight: 500;
}

/* 🔥 Список дат */
.dates-list {
	padding: 16px 20px;
	max-height: 210px;
	flex: 1;
	overflow-y: auto;
	scrollbar-width: none;
	-ms-overflow-style: none;
	-webkit-overflow-scrolling: touch;
	min-height: 0;
}

.dates-list::-webkit-scrollbar {
	width: 0;
	height: 0;
	display: none;
}

/* Строка с датой */
.date-row {
	display: grid;
	grid-template-columns: 14px 90px 1fr auto;
	align-items: flex-start;
	gap: 12px;
	padding: 14px 0;
	border-bottom: 1px solid #f0f0f0;
}

.date-row:last-child {
	border-bottom: none;
}

.date-row.is-live {
	background: linear-gradient(
		90deg,
		rgba(39, 174, 96, 0.1) 0%,
		transparent 100%
	);
	margin: 0 -20px;
	padding: 14px 20px;
	border-radius: 10px;
	border-bottom: none;
}

.date-row.is-cancelled {
	opacity: 0.5;
}

/* 🔥 Цветная точка */
.status-dot {
	width: 10px;
	height: 10px;
	border-radius: 50%;
	flex-shrink: 0;
}

.status-dot.pulse {
	animation: pulse 1.5s ease-in-out infinite;
}

.status-dot.gray {
	background: #95a5a6 !important;
}

.status-dot.orange {
	background: #f39c12 !important;
}

@keyframes pulse {
	0%,
	100% {
		opacity: 1;
		transform: scale(1);
	}
	50% {
		opacity: 0.6;
		transform: scale(1.2);
	}
}

/* Время */
.time-block {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 2px;
	flex-shrink: 0;
	min-width: 110px;
	line-height: 1.2;
}

.time {
	font-size: 15px;
	font-weight: 600;
	color: #1a1a2e;
}

.date {
	font-size: 12px;
	color: #888;
	display: block;
	margin-top: 2px;
	line-height: 1.2;
}

/* Название */
.conf-name {
	flex: 1;
	font-size: 13px;
	color: #555;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	min-width: 0; /* важно для text-overflow */
}

/* 🔥 Кнопки действий */
.btn-action {
	padding: 6px 14px;
	border-radius: 16px;
	font-size: 12px;
	font-weight: 500;
	border: none;
	white-space: nowrap;
	flex-shrink: 0;
	cursor: pointer;
	transition: all 0.2s;
}

.btn-action.live {
	background: #27ae60;
	color: #fff;
	animation: pulse-btn 2s ease-in-out infinite;
}

.btn-action.live:hover {
	background: #219a52;
	transform: scale(1.05);
}

.btn-action.join {
	background: #27ae60;
	color: #fff;
}

.btn-action.join:hover {
	background: #219a52;
	transform: scale(1.05);
}

@keyframes pulse-btn {
	0%,
	100% {
		box-shadow: 0 0 0 0 rgba(39, 174, 96, 0.4);
	}
	50% {
		box-shadow: 0 0 0 8px rgba(39, 174, 96, 0);
	}
}

.btn-status {
	padding: 6px 14px;
	border-radius: 16px;
	font-size: 12px;
	font-weight: 500;
	white-space: nowrap;
	flex-shrink: 0;
}

.btn-status.scheduled {
	background: #f0f0f0;
	color: #888;
}

.btn-status.soon {
	background: #fffbeb;
	color: #b45309;
}

.btn-status.ended {
	background: #f5f5f5;
	color: #aaa;
}

.btn-status.cancelled {
	background: #fff5f5;
	color: #e74c3c;
}

.btn-status.live {
	background: #e8f5e9;
	color: #27ae60;
}

/* Футер карточки */
.card-footer {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 14px 24px;
	background: #fafafa;
	border-top: 1px solid #f0f0f0;
	margin-top: auto;
}

.conf-count {
	font-size: 13px;
	color: #888;
}

.manage-link {
	font-size: 13px;
	color: #0096ed;
	text-decoration: none;
	font-weight: 500;
	transition: color 0.2s;
}

.manage-link:hover {
	color: #0085d4;
	text-decoration: underline;
}

/* ============================================
   МОДАЛКИ
   ============================================ */
.modal-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: rgba(0, 0, 0, 0.5);
	backdrop-filter: blur(4px);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
}

.modal-content {
	background: white;
	border-radius: 20px;
	max-width: 500px;
	width: 90%;
	max-height: 80vh;
	overflow: hidden;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.modal-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 24px 28px;
	border-bottom: 1px solid #f0f0f0;
}

.modal-header h3 {
	margin: 0;
	font-size: 20px;
}

.close-btn {
	width: 36px;
	height: 36px;
	border: none;
	background: #f0f0f0;
	border-radius: 50%;
	font-size: 22px;
	cursor: pointer;
	transition: all 0.2s;
	display: flex;
	align-items: center;
	justify-content: center;
}

.close-btn:hover {
	background: #e0e0e0;
}

.courses-list {
	max-height: 400px;
	overflow-y: auto;
}

.course-option {
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
	padding: 18px 28px;
	border: none;
	background: none;
	text-align: left;
	cursor: pointer;
	border-bottom: 1px solid #f0f0f0;
	transition: background 0.2s;
}

.course-option:hover {
	background: #f2f8ff;
}

.course-name {
	font-weight: 500;
	font-size: 16px;
}

.course-conferences-count {
	font-size: 14px;
	color: #666;
}

.no-courses {
	padding: 50px 28px;
	text-align: center;
}

.no-courses p {
	color: #666;
	font-size: 16px;
}

.create-course-link {
	display: inline-block;
	margin-top: 20px;
	color: #0096ed;
	font-weight: 500;
	font-size: 16px;
}

/* Подтверждение удаления */
.confirm-delete .modal-body {
	padding: 28px;
}

.confirm-delete .warning {
	color: #e74c3c;
	font-size: 14px;
	margin-top: 12px;
}

.modal-footer {
	display: flex;
	justify-content: flex-end;
	gap: 14px;
	padding: 20px 28px;
	border-top: 1px solid #f0f0f0;
}

.btn-cancel {
	padding: 12px 24px;
	border: 1px solid #e0e0e0;
	background: white;
	border-radius: 10px;
	cursor: pointer;
	font-size: 15px;
	transition: all 0.2s;
}

.btn-cancel:hover {
	background: #f5f5f5;
}

.btn-delete {
	padding: 12px 24px;
	border: none;
	background: #e74c3c;
	color: white;
	border-radius: 10px;
	cursor: pointer;
	font-size: 15px;
	transition: all 0.2s;
}

.btn-delete:hover {
	background: #c0392b;
}

.btn-delete:disabled {
	opacity: 0.6;
	cursor: not-allowed;
}

/* Анимации */
.dropdown-enter-active,
.dropdown-leave-active {
	transition: opacity 0.2s, transform 0.2s;
}

.dropdown-enter-from,
.dropdown-leave-to {
	opacity: 0;
	transform: translateY(-10px);
}

/* ============================================
   🔥 АДАПТИВНОСТЬ
   ============================================ */
@media (max-width: 1200px) {
	.cards-track {
		justify-content: flex-start;
		padding-left: 20px;
	}
}

@media (max-width: 900px) {
	.section-header {
		padding: 0 10px;
	}

	.header-controls {
		width: 100%;
		justify-content: center;
	}

	.dash-conf-card {
		width: 340px;
	}
}

@media (max-width: 768px) {
	.section-container {
		padding: 0 16px;
	}

	.section-title {
		font-size: 24px;
	}

	.header-controls {
		flex-direction: column;
		gap: 12px;
	}

	.dash-conf-card {
		width: 320px;
	}

	.date-row {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 12px 0;
		border-bottom: 1px solid #f0f0f0;
	}

	.conf-name {
		display: none;
	}

	.btn-action,
	.btn-status {
		grid-column: 2 / -1;
		justify-self: start;
		margin-top: 6px;
	}
}

@media (max-width: 480px) {
	.section-title {
		font-size: 22px;
	}

	.filter-button,
	.toggle-past {
		font-size: 14px;
		padding: 8px 14px;
	}

	.dash-conf-card {
		width: 290px;
	}

	.card-header {
		padding: 16px 18px;
	}

	.card-title {
		font-size: 15px;
	}
}

/* ============================================
   🔥 СЧЁТЧИК УЧАСТНИКОВ
   ============================================ */
.participants-text {
	font-size: 12px;
	color: #1a1a2e;
	white-space: nowrap;
}

.duration-timer {
	color: #27ae60;
	font-weight: 600;
	font-family: 'Consolas', 'Monaco', monospace;
	margin-left: 4px;
}
</style>
